import { useEffect, useRef } from "react";
import "./index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import api from "./lib/api";
import { toast } from "sonner";

import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Categories from "./pages/Categories";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import Forbidden from "./pages/Forbidden";

import MemberDashboard from "./pages/member/Dashboard";
import MemberProducts from "./pages/member/MyProducts";
import MemberDownloads from "./pages/member/Downloads";
import MemberProfile from "./pages/member/Profile";

import AdminDashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import ProductForm from "./pages/admin/ProductForm";
import AdminCategories from "./pages/admin/Categories";
import AdminMembers from "./pages/admin/Members";
import AdminAdmins from "./pages/admin/Admins";
import MemberApprovals from "./pages/admin/MemberApprovals";
import AdminDownloads from "./pages/admin/Downloads";
import AdminMessages from "./pages/admin/Messages";
import AdminBanners from "./pages/admin/Banners";
import AdminPages from "./pages/admin/Pages";
import AdminSettings from "./pages/admin/Settings";
import AdminProfile from "./pages/admin/Profile";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) {
      navigate("/login");
      return;
    }
    api
      .post("/auth/google/session", { session_id: sid })
      .then((r) => {
        login(r.data.user, r.data.token);
        window.history.replaceState(null, "", window.location.pathname);
        toast.success("Login berhasil");
        navigate(r.data.user.role === "admin" ? "/admin/dashboard" : "/member/dashboard");
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Login Google gagal");
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login");
      });
  }, []);

  return (
    <div className="min-h-screen grid place-items-center">
      <div className="text-center">
        <div className="h-10 w-10 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-500">Memproses login...</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/products" element={<Products />} />
      <Route path="/products/:slug" element={<ProductDetail />} />
      <Route path="/categories" element={<Categories />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/403" element={<Forbidden />} />

      <Route path="/member/dashboard" element={<ProtectedRoute><MemberDashboard /></ProtectedRoute>} />
      <Route path="/member/products" element={<ProtectedRoute><MemberProducts /></ProtectedRoute>} />
      <Route path="/member/downloads" element={<ProtectedRoute><MemberDownloads /></ProtectedRoute>} />
      <Route path="/member/profile" element={<ProtectedRoute><MemberProfile /></ProtectedRoute>} />

      <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute role="admin"><AdminProducts /></ProtectedRoute>} />
      <Route path="/admin/products/create" element={<ProtectedRoute role="admin"><ProductForm /></ProtectedRoute>} />
      <Route path="/admin/products/:slug/edit" element={<ProtectedRoute role="admin"><ProductForm /></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute role="admin"><AdminCategories /></ProtectedRoute>} />
      <Route path="/admin/members" element={<ProtectedRoute role="admin"><AdminMembers /></ProtectedRoute>} />
      <Route path="/admin/member-approvals" element={<ProtectedRoute role="admin"><MemberApprovals /></ProtectedRoute>} />
      <Route path="/admin/admins" element={<ProtectedRoute role="admin"><AdminAdmins /></ProtectedRoute>} />
      <Route path="/admin/downloads" element={<ProtectedRoute role="admin"><AdminDownloads /></ProtectedRoute>} />
      <Route path="/admin/messages" element={<ProtectedRoute role="admin"><AdminMessages /></ProtectedRoute>} />
      <Route path="/admin/banners" element={<ProtectedRoute role="admin"><AdminBanners /></ProtectedRoute>} />
      <Route path="/admin/pages" element={<ProtectedRoute role="admin"><AdminPages /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute role="admin"><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/profile" element={<ProtectedRoute role="admin"><AdminProfile /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
